import { ApolloError } from "@apollo/client";
import { Box } from "@material-ui/core";
import { ICashsaleFormValues } from "@saleor/createOrder/components/CashSaleForm/types";
import {
  CipChangeOrderItemDetailsInput,
  CipOrderUpdateInput,
  ScgOrderStatus,
  useCipChangeOrderAddNewOrderLineMutation,
  useCipChangeOrderUpdateMutation,
  useUpdateCipOrderMutation
} from "@saleor/graphql";
import { UseNavigatorResult } from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { FormikProps } from "formik";
import React, { Dispatch, SetStateAction } from "react";

import { getNotRefCreateOrderPath } from "../urls";
import {
  checkHasPriceAffectingChanges,
  cipAddItemDetailsPayload,
  clearLocalOrderIds,
  deleteLocalOrderId,
  findChangeOrderCashSaleEditedField,
  prepareHeaderPayload,
  prepareItemDetailsPayload,
  removeEmptyObjects,
  setLocalOrderId,
  submitOrderInformationMapping,
  submitOrderLineMapping,
  submitOrderOrganizationMapping
} from "../utils";
import { ISubmitState } from "../views/createOrChangeNotRefContractView";
interface Props {
  orderId: string;
  formikItemDetails: FormikProps<{ lines: NotRefOrderLine[] }>;
  formikHeader: FormikProps<NotRefOrderHeader>;
  formikCashSale: FormikProps<ICashsaleFormValues>;
  setSnackbarState: Dispatch<SetStateAction<any>>;
  ModeUpdate: any;
  navigate: UseNavigatorResult;
  setSubmitState: Dispatch<SetStateAction<ISubmitState>>;
  resetSubmitState: () => void;
  handleSubmitResponse: (responseData, errors) => void;
  currentAction: "none" | "add" | "split" | "edit";
  totalItemsOriginal?: number;
  orderAmountAfterVat?: number;
  hasCalculatedPrice?: boolean;
  isCashSaleOrder?: boolean;
  isEditableCashSale?: boolean;
}

const useSaveAndSubmitOrder = ({
  orderId,
  formikItemDetails,
  formikHeader,
  formikCashSale,
  setSnackbarState,
  ModeUpdate,
  navigate,
  setSubmitState,
  resetSubmitState,
  handleSubmitResponse,
  currentAction,
  totalItemsOriginal,
  orderAmountAfterVat,
  hasCalculatedPrice,
  isCashSaleOrder,
  isEditableCashSale
}: Props) => {
  const notify = useNotifier();

  const [updateCipOrderMutation] = useUpdateCipOrderMutation({
    onError: () => {
      //
    }
  });

  const [cipChangeOrderUpdate] = useCipChangeOrderUpdateMutation({
    onError: () => {
      //
    }
  });
  const [cipAddNewProductUpdate] = useCipChangeOrderAddNewOrderLineMutation({
    onError: () => {
      //
    }
  });

  const otherHeaderFields = formikHeader => ({
    poNumber: formikHeader.values.poNumber
  });
  const getupdateCipOrderVariables = (
    status: ScgOrderStatus
  ): {
    id: string;
    input: CipOrderUpdateInput;
  } => ({
    id: orderId || "",
    input: {
      lines: submitOrderLineMapping(
        formikItemDetails.values.lines,
        otherHeaderFields(formikHeader)
      ),
      orderInformation: submitOrderInformationMapping(formikHeader.values),
      orderOrganizationData: submitOrderOrganizationMapping(
        formikHeader.values
      ),
      status
    }
  });

  const getUpdatecipChangeOrderVariables = () => {
    const totalItems = totalItemsOriginal;
    const headerPayload = prepareHeaderPayload(
      formikHeader.values,
      formikHeader,
      orderId,
      totalItems
    );
    const itemDetailsPayload = JSON.stringify(
      prepareItemDetailsPayload(
        formikItemDetails.values.lines,
        formikItemDetails
      )
    );

    const filteredItemDetailsPayload: CipChangeOrderItemDetailsInput[] = removeEmptyObjects(
      JSON.parse(itemDetailsPayload)
    );
    if (
      Object.keys(headerPayload).length > 1 ||
      filteredItemDetailsPayload.length > 0
    ) {
      return {
        input: {
          headerDetails: headerPayload,
          itemDetails: filteredItemDetailsPayload
        }
      };
    }
  };

  const getCipAddNewProductVariables = () => ({
    input: {
      headerDetails: {
        soNo: orderId,
        totalItems: totalItemsOriginal
      },
      itemDetails: cipAddItemDetailsPayload(formikItemDetails.values.lines)
    }
  });

  const validateAddNewItem = formik => {
    const lines = [...formik.values.lines];
    const newItems = lines.filter(line => line?.isNewItem);
    const errors = formik.errors?.lines;

    for (let i = 0; i < newItems?.length; i++) {
      if (
        errors?.[i]?.quantity ||
        errors?.[i]?.requestDate ||
        (currentAction !== "add" && errors?.[i]?.saleQtyFactor) ||
        errors?.[i]?.batchNo
      ) {
        return false;
      }
    }
    return true;
  };

  const saveDraft = (showSnack = true) => {
    updateCipOrderMutation({
      variables: getupdateCipOrderVariables(ScgOrderStatus.DRAFT)
    }).then(({ data, errors }) => {
      if (data?.updateCipOrder?.order?.id) {
        setSnackbarState({
          open: showSnack,
          success: true,
          mode: ModeUpdate.SaveDraft
        });
        formikHeader.setFieldValue("id", data?.updateCipOrder?.order?.id);
        deleteLocalOrderId(formikHeader?.values?.soldToCode);
        navigate(getNotRefCreateOrderPath(data?.updateCipOrder?.order?.id), {
          replace: true
        });
      }
      if (data?.updateCipOrder.errors.length || errors) {
        setSnackbarState({
          open: showSnack,
          success: false,
          mode: ModeUpdate.SaveDraft
        });
      }
    });
  };

  const createPreDraft = () =>
    updateCipOrderMutation({
      variables: getupdateCipOrderVariables(ScgOrderStatus.PRE_DRAFT)
    })
      .then(({ data, errors }) => {
        if (data?.updateCipOrder?.order?.id) {
          setLocalOrderId(
            formikHeader.values.soldToCode,
            data?.updateCipOrder?.order?.id
          );
          formikHeader.setFieldValue("id", data?.updateCipOrder?.order?.id);
          navigate(getNotRefCreateOrderPath(data?.updateCipOrder?.order?.id), {
            replace: true
          });
          return true;
        }
        if (data?.updateCipOrder.errors.length) {
          return false;
        }
        if (errors.length) {
          return false;
        }
        return false;
      })
      .catch(() => false);

  const validateItemDetails = formik => {
    const lines = [...formik.values.lines];
    const errors = formik.errors?.lines;
    for (let i = 0; i < lines?.length; i++) {
      if (
        (!lines[i]?.parentItemNo &&
          (errors?.[i]?.quantity ||
            errors?.[i]?.requestDate ||
            errors?.[i]?.pricePerUnit)) ||
        errors?.[i]?.batchNo
      ) {
        return true;
      }
    }
    return false;
  };

  function navigateWithQueryParams(
    queryParams: URLSearchParams,
    typeName?: string
  ) {
    queryParams.delete("type");
    if (typeName) {
      queryParams.set("type", typeName);
    }
    const queryString = queryParams.toString();
    navigate(`?${queryString}`, { replace: true });
  }

  function checkShouldValidatePriceCalculation() {
    const { cashFlag, balanceFlag } = formikCashSale?.values ?? {};
    return isCashSaleOrder && (!!cashFlag || !!balanceFlag);
  }

  async function validateBeforeSubmitOrder() {
    const v1 = await formikHeader.validateForm();
    const v2 = validateItemDetails(formikItemDetails);
    const v3 = isCashSaleOrder ? await formikCashSale.validateForm() : null;

    const hasFormikHeaderErrors = v1 && Object.keys(v1).length > 0;
    const hasFormikCashSaleErrors = v3 && Object.keys(v3).length > 0;

    if (hasFormikHeaderErrors || v2 || hasFormikCashSaleErrors) {
      setSubmitState({
        state: "validationError",
        context: {
          v1: hasFormikHeaderErrors,
          v2,
          v3: hasFormikCashSaleErrors
        }
      });
      return false;
    }

    const shouldValidatePriceCalculation = checkShouldValidatePriceCalculation();
    if (shouldValidatePriceCalculation && orderAmountAfterVat <= 0) {
      setSubmitState({ state: "noPriceCalculationPopup", context: null });
      return false;
    }

    return true;
  }

  async function submitOrder() {
    setSubmitState({ state: "processing", context: null });

    const queryParams = new URLSearchParams(location.search);
    navigateWithQueryParams(queryParams, "submitted");
    await updateCipOrderMutation({
      variables: getupdateCipOrderVariables(ScgOrderStatus.CONFIRMED)
    })
      .then(async ({ data, errors }) => {
        // บังคับให้ `errors` เป็นประเภท `ApolloError` เพื่อการใช้งานได้ถูกต้องตามที่ได้รับ
        const apolloError = (errors as unknown) as ApolloError;
        const timeoutErrorMessage = "Error Code : 504 - Gateway Timeout";
        const hasTimeoutError =
          apolloError?.message === timeoutErrorMessage ||
          apolloError?.graphQLErrors?.some(
            error => error.message === timeoutErrorMessage
          );
        if (hasTimeoutError) {
          clearLocalOrderIds();
          navigateWithQueryParams(queryParams, "timeout");
          return;
        }

        navigateWithQueryParams(queryParams);
        const responseData = data?.updateCipOrder;
        handleSubmitResponse(responseData, errors);
      })
      .catch(() => {
        navigateWithQueryParams(queryParams);
        resetSubmitState();
      });
  }

  const validateChangeOrderItemDetails = formik => {
    const lines = [...formik.values.lines];
    const errors = formik.errors?.lines;
    for (let i = 0; i < lines?.length; i++) {
      if (
        (!lines[i]?.parentItemNo &&
          lines[i]?.itemStatusEn !== "Cancelled (93)" &&
          (errors?.[i]?.quantity ||
            errors?.[i]?.requestDate ||
            errors?.[i]?.pricePerUnit)) ||
        errors?.[i]?.batchNo
      ) {
        return true;
      }
    }
    return false;
  };

  async function validateBeforeSaveChangOrder() {
    const v1 = await formikHeader.validateForm();
    const v2 = validateChangeOrderItemDetails(formikItemDetails);
    const v3 =
      isCashSaleOrder && isEditableCashSale
        ? await formikCashSale.validateForm()
        : null;

    const hasFormikHeaderErrors = v1 && Object.keys(v1).length > 0;
    const hasFormikCashSaleErrors = v3 && Object.keys(v3).length > 0;

    if (hasFormikHeaderErrors || v2 || hasFormikCashSaleErrors) {
      setSubmitState({
        state: "validationError",
        context: {
          v1: hasFormikHeaderErrors,
          v2,
          v3: hasFormikCashSaleErrors
        }
      });
      return false;
    }

    const changeOrderVariables = getUpdatecipChangeOrderVariables();
    const {
      isEdited: hasCashSaleFormChanged
    } = findChangeOrderCashSaleEditedField(formikCashSale);

    if (!changeOrderVariables && !hasCashSaleFormChanged) {
      notify({
        status: "warning",
        title: "No Changes to Submit",
        text: (
          <Box>{`You have not made any changes which qualifies to be submitted`}</Box>
        )
      });
      return false;
    }

    const shouldValidatePriceCalculation = checkShouldValidatePriceCalculation();
    const hasPriceAffectingChanges = checkHasPriceAffectingChanges(
      formikItemDetails
    );

    if (
      shouldValidatePriceCalculation &&
      hasPriceAffectingChanges &&
      !hasCalculatedPrice
    ) {
      setSubmitState({ state: "noPriceCalculationPopup", context: null });
      return false;
    }

    return true;
  }

  async function saveChangOrder() {
    setSubmitState({ state: "processing", context: null });

    const changeOrderVariables = getUpdatecipChangeOrderVariables();
    cipChangeOrderUpdate({
      variables: changeOrderVariables
    })
      .then(({ data, errors }) => {
        const responseData = data?.cipChangeOrderUpdate;
        handleSubmitResponse(responseData, errors);
      })
      .catch(() => {
        resetSubmitState();
      });
  }

  async function validateBeforeSaveAddNewCipProduct() {
    if (!validateAddNewItem(formikItemDetails)) {
      setSubmitState({
        state: "validationError",
        context: {}
      });
      return false;
    }

    return true;
  }

  async function saveAddNewCipProduct() {
    setSubmitState({ state: "processing", context: null });

    cipAddNewProductUpdate({
      variables: getCipAddNewProductVariables()
    })
      .then(({ data, errors }) => {
        const responseData = data?.cipChangeOrderAddNewOrderLine;
        handleSubmitResponse(responseData, errors);
      })
      .catch(() => {
        resetSubmitState();
      });
  }

  const onSubmitCancel = () => {
    saveDraft(false);
    resetSubmitState();
  };

  return {
    saveDraft,
    createPreDraft,
    onSubmitCancel,

    validateBeforeSubmitOrder,
    submitOrder,

    validateBeforeSaveChangOrder,
    saveChangOrder,

    validateBeforeSaveAddNewCipProduct,
    saveAddNewCipProduct
  };
};

export default useSaveAndSubmitOrder;
